import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { createPortal } from "react-dom";
import { cn } from "cn";

const MorphPopoverContext = React.createContext(null);

const MORPH_TRANSITION = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.6,
};

function useMorphPopover(componentName) {
  const context = React.useContext(MorphPopoverContext);
  if (!context) {
    throw new Error(`${componentName} must be used within <MorphPopover>`);
  }
  return context;
}

function MorphPopover({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  className,
}) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const triggerId = React.useId();
  const contentId = React.useId();
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (nextOpen) => {
      const value = typeof nextOpen === "function" ? nextOpen(open) : nextOpen;
      if (!controlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlled, onOpenChange, open],
  );

  React.useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, setOpen]);

  const context = React.useMemo(
    () => ({
      contentId,
      contentRef,
      open,
      setOpen,
      triggerId,
      triggerRef,
    }),
    [contentId, open, setOpen, triggerId],
  );

  return (
    <MorphPopoverContext.Provider value={context}>
      <div className={cn("contents", className)} data-slot="morph-popover">
        {children}
      </div>
    </MorphPopoverContext.Provider>
  );
}

function MorphPopoverTrigger({ children }) {
  const context = useMorphPopover("MorphPopoverTrigger");
  const child = React.Children.only(children);

  if (!React.isValidElement(child)) {
    throw new Error("MorphPopoverTrigger requires a single valid element child.");
  }

  const childRef = child.ref;
  return React.cloneElement(child, {
    "aria-controls": context.contentId,
    "aria-expanded": context.open,
    "data-state": context.open ? "open" : "closed",
    id: child.props.id ?? context.triggerId,
    onClick: (event) => {
      child.props.onClick?.(event);
      if (!event.defaultPrevented) context.setOpen((value) => !value);
    },
    ref: (node) => {
      context.triggerRef.current = node;
      if (typeof childRef === "function") childRef(node);
      else if (childRef) childRef.current = node;
    },
  });
}

function getMorphClip(side, radius, open) {
  if (open) return `inset(0% 0% 0% 0% round ${radius}px)`;
  if (side === "top") return `inset(100% 0% 0% 0% round ${radius}px)`;
  if (side === "left") return `inset(0% 0% 0% 100% round ${radius}px)`;
  if (side === "right") return `inset(0% 100% 0% 0% round ${radius}px)`;
  return `inset(0% 0% 100% 0% round ${radius}px)`;
}

function getMorphPosition({ align, content, side, sideOffset, trigger }) {
  const viewportPadding = 8;
  let left = trigger.left;
  let top = trigger.bottom + sideOffset;

  if (side === "top") {
    top = trigger.top - content.height - sideOffset;
  } else if (side === "right") {
    left = trigger.right + sideOffset;
    top = align === "start"
      ? trigger.top
      : trigger.bottom - content.height;
  } else if (side === "left") {
    left = trigger.left - content.width - sideOffset;
    top = align === "start"
      ? trigger.top
      : trigger.bottom - content.height;
  } else if (align === "end") {
    left = trigger.right - content.width;
  }

  return {
    left: Math.max(viewportPadding, Math.min(left, window.innerWidth - content.width - viewportPadding)),
    top: Math.max(viewportPadding, Math.min(top, window.innerHeight - content.height - viewportPadding)),
  };
}

function MorphPopoverContent({
  children,
  side = "bottom",
  align = "end",
  sideOffset = 8,
  radius = 16,
  className,
  ...props
}) {
  const context = useMorphPopover("MorphPopoverContent");
  const reduceMotion = useReducedMotion() ?? false;
  const [position, setPosition] = React.useState(null);

  React.useLayoutEffect(() => {
    if (!context.open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = context.triggerRef.current;
      const content = context.contentRef.current;
      if (!trigger || !content) return;

      const triggerRect = trigger.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      setPosition(
        getMorphPosition({
          align,
          content: {
            height: contentRect.height,
            width: contentRect.width,
          },
          side,
          sideOffset,
          trigger: {
            bottom: triggerRect.bottom,
            left: triggerRect.left,
            right: triggerRect.right,
            top: triggerRect.top,
          },
        }),
      );
    };

    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, context.open, context.contentRef, context.triggerRef, side, sideOffset]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence initial={false}>
      {context.open ? (
        <motion.div
          {...props}
          ref={context.contentRef}
          aria-labelledby={context.triggerId}
          data-slot="morph-popover-content"
          initial={{
            clipPath: getMorphClip(side, radius, false),
            opacity: 0,
            scale: 0.96,
          }}
          animate={{
            clipPath: getMorphClip(side, radius, true),
            opacity: 1,
            scale: 1,
          }}
          exit={{
            clipPath: getMorphClip(side, radius, false),
            opacity: 0,
            scale: 0.96,
          }}
          transition={reduceMotion ? { duration: 0 } : MORPH_TRANSITION}
          className={cn("ui-morph-popover-content", className)}
          style={{
            position: "fixed",
            zIndex: 1000,
            left: position?.left ?? 0,
            opacity: position ? undefined : 0,
            top: position?.top ?? 0,
            transformOrigin: side === "right" ? "left center" : side === "left" ? "right center" : side === "top" ? "center bottom" : "center top",
            visibility: position ? "visible" : "hidden",
          }}>
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

function Popover({ ...props }) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  ...props
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50">
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            "z-50 flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className,
          )}
          {...props} />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverHeader({ className, ...props }) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-0.5 text-sm", className)}
      {...props} />
  );
}

function PopoverTitle({ className, ...props }) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("font-medium", className)}
      {...props} />
  );
}

function PopoverDescription({ className, ...props }) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("text-muted-foreground", className)}
      {...props} />
  );
}

export {
  MorphPopover,
  MorphPopoverContent,
  MorphPopoverTrigger,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
